import cx from "classnames";
import { useEffect, useRef } from "react";
import { Post } from "store/posts/postConstants";
import usePostSelectionContext from "../PostSelection/usePostSelectionContext";
import useUpdatePostActions from "../utils/useUpdatePostActions";
import PostCardHeader from "./Header/PostCardHeader";
import PublishDetailsAlert from "./Header/PublishDetailsAlert";
import ReviewStatusBanner from "./Header/ReviewStatusBanner";
import PostCardContent from "./PostCardContent";
import PostCardFooter from "./PostCardFooter";
import PostCardWrapper from "./PostCardWrapper";
import { usePostCardLogic } from "./utils/usePostCardLogic";
import { usePostCardMenu } from "./utils/usePostCardMenu";

const PostCard = ({ post }: { post: Post }) => {
  const { inSelectionMode, isSelected, selectPost } = usePostSelectionContext();
  const {
    loading,
    isLoading,
    updatePostReviewStatus,
    savePost,
    schedulePost,
    publishPost,
    discardPost,
  } = useUpdatePostActions();

  const {
    isShowingInputs,
    isDownloading,
    isUpdatingCategory,
    isFetchingPostDetails,
    postDetail,
    categories,
    isLoadingCategories,
    toggleShowInputs,
    handleToggleTheme,
    handleUpdateCategory,
    refetchPostDetails,
    postDetailData,
    setIsDownloading,
  } = usePostCardLogic(post);

  const postContainerRef = useRef<HTMLDivElement>(null);

  const {
    isMenuOpen,
    isMenuLoading,
    menuItems,
    handleMenuClick,
    handleMenuOpenChange,
    isRTL,
  } = usePostCardMenu({
    post,
    postDetail,
    isDownloading,
    setIsDownloading,
    isFetchingPostDetails,
    refetch: refetchPostDetails,
    toggleFlip: toggleShowInputs,
  });

  useEffect(() => {
    if (isMenuOpen && !isFetchingPostDetails && !postDetailData) {
      refetchPostDetails();
    }
  }, [isMenuOpen, isFetchingPostDetails, postDetailData, refetchPostDetails]);

  const disableCardInteractions = isLoading || isMenuLoading;
  const isPublishedPost = post.status === "PUBLISHED";

  return (
    <PostCardWrapper {...{ post, loadingPostAction: loading }}>
      {({ openPostEditor }) => (
        <div className="w-full mx-auto flex flex-col">
          <div>
            <div
              className={cx(
                "bg-antd-colorBgContainer border border-antd-colorBorder rounded-lg w-full max-w-sm shadow-sm relative mx-auto overflow-hidden",
                {
                  "outline outline-1 outline-antd-colorPrimary !border-antd-colorPrimary !bg-antd-colorPrimaryBg":
                    isSelected(post),
                }
              )}
              ref={postContainerRef}
              tabIndex={1}
              onClick={(_e) => {
                if (inSelectionMode) {
                  selectPost(post);
                }
              }}
            >
              <ReviewStatusBanner
                post={post}
                inSelectionMode={inSelectionMode}
              />

              {isPublishedPost && <PublishDetailsAlert post={post} />}

              {!inSelectionMode && (
                <PostCardHeader
                  post={post}
                  disabled={disableCardInteractions}
                  changeReviewStatus={(reviewStatus) =>
                    updatePostReviewStatus({ post, reviewStatus })
                  }
                  isMenuLoading={isMenuLoading}
                  isMenuOpen={isMenuOpen}
                  menuItems={menuItems}
                  handleMenuClick={handleMenuClick}
                  handleMenuOpenChange={handleMenuOpenChange}
                  toggleShowInputs={toggleShowInputs}
                  isShowingInputs={isShowingInputs}
                  canShowInputs={!!postDetail.inputs}
                  inSelectionMode={inSelectionMode}
                  isSelected={isSelected(post)}
                  selectPost={() => selectPost(post)}
                  onUpdateCategory={handleUpdateCategory}
                  isUpdatingCategory={isUpdatingCategory}
                  categories={categories}
                  isLoadingCategories={isLoadingCategories}
                />
              )}

              <div className="relative border-t border-b border-antd-colorBorderSecondary">
                <PostCardContent
                  isShowingInputs={isShowingInputs}
                  toggleShowInputs={toggleShowInputs}
                  post={post}
                  postDetail={postDetail}
                  handleToggleTheme={handleToggleTheme}
                  isRTL={isRTL}
                  onClick={() => {
                    if (!inSelectionMode && !isPublishedPost) {
                      openPostEditor();
                    }
                  }}
                />
              </div>

              {!inSelectionMode && !isPublishedPost && (
                <PostCardFooter
                  post={post}
                  openPostEditor={openPostEditor}
                  disabled={disableCardInteractions}
                  isLoading={isLoading}
                  savePost={savePost}
                  schedulePost={schedulePost}
                  publishPost={publishPost}
                  discardPost={discardPost}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </PostCardWrapper>
  );
};

export default PostCard;
